import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { learningAssetAPI, courseAPI, quizAPI, flashcardAPI, interviewAPI } from '../services/api';

function AssetLibrary() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const assetTypes = [
    { value: '', label: 'All Types' },
    { value: 'video', label: '� Video' },
    { value: 'document', label: '📄 Document' },
    { value: 'quiz', label: '📝 Quiz' },
    { value: 'flashcard', label: '🎴 Flashcard' },
    { value: 'interview', label: '🎤 Interview' },
    { value: 'link', label: '🔗 Link' },
    { value: 'note', label: '📋 Note' }
  ];

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Access denied. Admin only.');
      navigate('/');
    } else {
      fetchAssets();
      fetchCourses();
    }
  }, [user, navigate, page, selectedCourse, selectedType, searchTerm, sortColumn, sortDirection]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        sortBy: sortColumn,
        sortDirection: sortDirection
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCourse) params.append('courseId', selectedCourse);
      if (selectedType) params.append('assetType', selectedType);

      const response = await learningAssetAPI.getAllAssets(params.toString());
      
      if (response.data && Array.isArray(response.data.assets)) {
        setAssets(response.data.assets);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setAssets([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching assets:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch assets';
      setError(`Error: ${errorMsg}`);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getCourseList(1, 100);
      setCourses(response.data.courses || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handlePreview = async (asset) => {
    setSelectedAsset(asset);
    setShowPreviewModal(true);
    
    // Fetch referenced content if exists
    if (asset.contentReference?.refId) {
      try {
        let content = null;
        switch (asset.contentReference.refModel) {
          case 'Quiz':
            const quizRes = await quizAPI.getQuizList();
            content = quizRes.data.quizzes.find(q => q._id === asset.contentReference.refId);
            break;
          case 'FlashCard':
            const fcRes = await flashcardAPI.getFlashcardSets();
            content = fcRes.data.flashcardSets.find(f => f._id === asset.contentReference.refId);
            break;
          case 'Interview':
            const intRes = await interviewAPI.getInterviews();
            content = intRes.data.interviews.find(i => i._id === asset.contentReference.refId);
            break;
        }
        setPreviewContent(content);
      } catch (err) {
        console.error('Error fetching content:', err);
      }
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset? This will remove it from the course.')) {
      return;
    }

    try {
      await learningAssetAPI.deleteAsset(assetId);
      fetchAssets();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete asset');
    }
  };

  const handleEdit = (asset) => {
    // Navigate to course builder and scroll to asset
    navigate(`/course/${asset.course._id}/build`, { state: { highlightAsset: asset._id } });
  };

  const getAssetIcon = (type) => {
    const icons = {
      video: '🎥',
      document: '📄',
      quiz: '📝',
      flashcard: '🎴',
      interview: '🎤',
      link: '🔗',
      note: '📋'
    };
    return icons[type] || '📦';
  };

  const getFileSize = (bytes) => {
    if (!bytes) return '-';
    const mb = bytes / 1024 / 1024;
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(2)} KB`;
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <span style={{ color: '#cbd5e0', marginLeft: '4px' }}>⇅</span>;
    }
    return <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  if (loading && assets.length === 0) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#718096' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <p>Loading asset library...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>
            📚 Asset Library
          </h2>
          <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
            Centralized view of all learning assets across courses
          </p>
        </div>

      {error && (
        <div style={{ 
          padding: '12px 16px', 
          background: '#fef2f2', 
          color: '#ef4444', 
          borderRadius: '8px', 
          marginBottom: '24px',
          border: '1px solid #fecaca'
        }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div style={{ 
        background: '#ffffff', 
        padding: '20px', 
        borderRadius: '12px', 
        marginBottom: '24px',
        display: 'grid',
        gap: '16px',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div>
          <label style={{ display: 'block', color: '#4a5568', fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
            Search
          </label>
          <input
            type="text"
            placeholder="Search by title or description..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: '#ffffff',
              border: '1px solid #cbd5e0',
              borderRadius: '6px',
              color: '#2d3748',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', color: '#4a5568', fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
            Course
          </label>
          <select
            value={selectedCourse}
            onChange={(e) => {
              setSelectedCourse(e.target.value);
              setPage(1);
            }}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: '#ffffff',
              border: '1px solid #cbd5e0',
              borderRadius: '6px',
              color: '#2d3748',
              fontSize: '14px'
            }}
          >
            <option value="">All Courses</option>
            {courses.map(course => (
              <option key={course._id} value={course._id}>
                {course.title} ({course.code})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', color: '#4a5568', fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
            Asset Type
          </label>
          <select
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e.target.value);
              setPage(1);
            }}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: '#ffffff',
              border: '1px solid #cbd5e0',
              borderRadius: '6px',
              color: '#2d3748',
              fontSize: '14px'
            }}
          >
            {assetTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          padding: '20px', 
          borderRadius: '12px' 
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#fff', marginBottom: '4px' }}>
            {assets.length}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
            Total Assets
          </div>
        </div>

        <div style={{ 
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
          padding: '20px', 
          borderRadius: '12px' 
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#fff', marginBottom: '4px' }}>
            {courses.length}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
            Courses
          </div>
        </div>

        <div style={{ 
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
          padding: '20px', 
          borderRadius: '12px' 
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#fff', marginBottom: '4px' }}>
            {new Set(assets.map(a => a.assetType)).size}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
            Asset Types
          </div>
        </div>
      </div>

      {/* Assets Table */}
      {assets.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px', 
          background: '#ffffff', 
          borderRadius: '12px',
          border: '2px dashed #cbd5e0'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>📦</div>
          <h3 style={{ color: '#2d3748', margin: '0 0 8px 0' }}>No assets found</h3>
          <p style={{ color: '#718096', margin: 0 }}>
            {searchTerm || selectedCourse || selectedType 
              ? 'Try adjusting your filters'
              : 'Create your first learning asset in a course'}
          </p>
        </div>
      ) : (
        <>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            marginBottom: '24px'
          }}>
            {/* Table Info */}
            <div style={{
              padding: '12px 16px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#f7fafc'
            }}>
              <span style={{ fontSize: '14px', color: '#4a5568', fontWeight: '500' }}>
                Showing {assets.length} assets
              </span>
              <span style={{ fontSize: '13px', color: '#718096' }}>
                Page {page} of {totalPages}
              </span>
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th 
                      onClick={() => handleSort('title')}
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#4a5568',
                        cursor: 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Title <SortIcon column="title" />
                    </th>
                    <th 
                      onClick={() => handleSort('type')}
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#4a5568',
                        cursor: 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Type <SortIcon column="type" />
                    </th>
                    <th 
                      onClick={() => handleSort('course')}
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#4a5568',
                        cursor: 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Course <SortIcon column="course" />
                    </th>
                    <th 
                      onClick={() => handleSort('chapter')}
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#4a5568',
                        cursor: 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Chapter <SortIcon column="chapter" />
                    </th>
                    <th 
                      onClick={() => handleSort('createdAt')}
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#4a5568',
                        cursor: 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Created <SortIcon column="createdAt" />
                    </th>
                    <th style={{
                      padding: '12px 16px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#4a5568',
                      whiteSpace: 'nowrap'
                    }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map((asset, index) => (
                    <tr 
                      key={asset._id}
                      style={{
                        borderBottom: '1px solid #e2e8f0',
                        transition: 'background 0.15s',
                        background: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#edf2f7';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = index % 2 === 0 ? '#ffffff' : '#f9fafb';
                      }}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '20px' }}>{getAssetIcon(asset.assetType)}</span>
                          <div>
                            <div style={{ 
                              fontWeight: '500', 
                              color: '#1a202c',
                              marginBottom: '2px'
                            }}>
                              {asset.title}
                            </div>
                            {asset.description && (
                              <div style={{ 
                                fontSize: '12px', 
                                color: '#718096',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '300px'
                              }}>
                                {asset.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          background: '#edf2f7',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#2d3748',
                          textTransform: 'capitalize'
                        }}>
                          {asset.assetType}
                        </span>
                        {asset.isRequired && (
                          <span style={{ 
                            display: 'inline-block',
                            marginLeft: '6px',
                            padding: '4px 8px', 
                            background: '#fee', 
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#c53030'
                          }}>
                            Required
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#2d3748' }}>
                        {asset.course?.title || '-'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#718096' }}>
                        {asset.chapter?.title || '-'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#718096', whiteSpace: 'nowrap' }}>
                        {new Date(asset.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreview(asset);
                            }}
                            style={{
                              padding: '6px 10px',
                              fontSize: '12px',
                              background: '#667eea',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#5568d3';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#667eea';
                            }}
                            title="Preview"
                          >
                            👁️
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(asset);
                            }}
                            style={{
                              padding: '6px 10px',
                              fontSize: '12px',
                              background: '#48bb78',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#38a169';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#48bb78';
                            }}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(asset._id);
                            }}
                            style={{
                              padding: '6px 10px',
                              fontSize: '12px',
                              background: '#f56565',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#e53e3e';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#f56565';
                            }}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '8px',
              alignItems: 'center'
            }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn btn-secondary"
                style={{ 
                  padding: '8px 16px', 
                  fontSize: '14px',
                  opacity: page === 1 ? 0.5 : 1
                }}
              >
                ← Previous
              </button>
              <span style={{ color: '#4a5568', fontSize: '14px' }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn btn-secondary"
                style={{ 
                  padding: '8px 16px', 
                  fontSize: '14px',
                  opacity: page === totalPages ? 0.5 : 1
                }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedAsset && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.6)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => {
            setShowPreviewModal(false);
            setPreviewContent(null);
          }}
        >
          <div 
            style={{ 
              backgroundColor: '#ffffff', 
              borderRadius: '12px', 
              padding: '24px', 
              width: '90%', 
              maxWidth: '800px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'start',
              marginBottom: '20px'
            }}>
              <div>
                <h2 style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '24px', 
                  color: '#1a202c',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  {getAssetIcon(selectedAsset.assetType)} {selectedAsset.title}
                </h2>
                <p style={{ margin: 0, fontSize: '14px', color: '#718096' }}>
                  {selectedAsset.course?.title} 
                  {selectedAsset.chapter && ` • ${selectedAsset.chapter.title}`}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewContent(null);
                }}
                style={{
                  background: '#e2e8f0',
                  border: 'none',
                  color: '#2d3748',
                  fontSize: '24px',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ 
              background: '#f7fafc', 
              padding: '20px', 
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #e2e8f0'
            }}>
              {selectedAsset.description && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', color: '#718096', marginBottom: '8px', fontWeight: '500' }}>
                    Description
                  </div>
                  <div style={{ fontSize: '14px', color: '#2d3748' }}>
                    {selectedAsset.description}
                  </div>
                </div>
              )}

              {selectedAsset.assetType === 'note' && selectedAsset.content && (
                <div>
                  <div style={{ fontSize: '13px', color: '#718096', marginBottom: '8px', fontWeight: '500' }}>
                    Content
                  </div>
                  <div 
                    style={{ 
                      fontSize: '14px', 
                      color: '#2d3748',
                      background: '#ffffff',
                      padding: '16px',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0'
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedAsset.content }}
                  />
                </div>
              )}

              {selectedAsset.assetType === 'link' && selectedAsset.externalUrl && (
                <div>
                  <div style={{ fontSize: '13px', color: '#718096', marginBottom: '8px', fontWeight: '500' }}>
                    URL
                  </div>
                  <a 
                    href={selectedAsset.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '14px', color: '#667eea', textDecoration: 'underline' }}
                  >
                    {selectedAsset.externalUrl}
                  </a>
                </div>
              )}

              {selectedAsset.assetType === 'video' && selectedAsset.fileUrl && (
                <div>
                  <div style={{ fontSize: '13px', color: '#718096', marginBottom: '8px', fontWeight: '500' }}>
                    Video File
                  </div>
                  <video 
                    controls 
                    style={{ width: '100%', borderRadius: '6px', maxHeight: '400px' }}
                    src={selectedAsset.fileUrl}
                  />
                </div>
              )}

              {selectedAsset.assetType === 'document' && selectedAsset.fileUrl && (
                <div>
                  <div style={{ fontSize: '13px', color: '#718096', marginBottom: '8px', fontWeight: '500' }}>
                    Document
                  </div>
                  <div style={{ fontSize: '14px', color: '#2d3748' }}>
                    {selectedAsset.fileName} ({getFileSize(selectedAsset.fileSize)})
                  </div>
                  <a 
                    href={selectedAsset.fileUrl}
                    download
                    className="btn btn-primary"
                    style={{ marginTop: '12px', display: 'inline-block' }}
                  >
                    📥 Download
                  </a>
                </div>
              )}

              {previewContent && selectedAsset.contentReference && (
                <div>
                  <div style={{ fontSize: '13px', color: '#718096', marginBottom: '8px', fontWeight: '500' }}>
                    {selectedAsset.contentReference.refModel} Content
                  </div>
                  <div style={{ 
                    background: '#ffffff', 
                    padding: '16px', 
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#2d3748',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Title:</strong> {previewContent.title}
                    </div>
                    {previewContent.description && (
                      <div>
                        <strong>Description:</strong> {previewContent.description}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => handleEdit(selectedAsset)}
                className="btn btn-primary"
              >
                Edit in Course
              </button>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewContent(null);
                }}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default AssetLibrary;
